import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { Site } from '@/api/sites';
import { NpiProvider } from '@/api/npi-preferred';
import { useGetPreferredNpiBySiteId } from '@/hooks/api/use-get-preferred-npi-by-site-id';
import { formatNpiProviderName } from '@/utils/strings';
import { ALERT_CONFIG, swalert } from '@/libs/sweetalert';
import { useDeletePreferredProvider } from '@/hooks/api/use-delete-npi-preferred-provider';

import { Table } from '@/components/ui/table';
import { FetchStates } from '@/components/fetch-states';

import { SiteNpiSearch } from './site-npi-search';

export function SiteNPIEdit() {
  const location: { state?: { site?: Site } } = useLocation();
  const { site } = location.state ?? {};

  const { t } = useTranslation();

  const { data, loading, refreshing, refetch } = useGetPreferredNpiBySiteId({
    site_id: site?.site_id,
  });
  const { request: removePreferredRequest, loading: removingPreferred } =
    useDeletePreferredProvider();

  const formatProviderName = useCallback(formatNpiProviderName, []);

  const onRemovePreferred = async (provider: NpiProvider) => {
    if (!site) return;

    const result = await swalert.fire({
      ...ALERT_CONFIG,
      title: t('npi-eliminate-confirm-title'),
      html: `<p style="margin-bottom: 1rem;">${t('npi-eliminate-confirm-description').replace('{npi_name}', `<span style="font-weight: bold">${formatProviderName(undefined, provider)}</span>`)}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('yes'),
      cancelButtonText: t('no'),
    });

    if (result.isConfirmed) {
      await removePreferredRequest({ site_id: site.site_id, npi_number: provider.number });
      void refetch();
    }
  };

  const headers = useMemo(
    () => [
      { key: 'number', label: t('npi-number'), type: 'value' as const },
      { key: 'enumeration_type', label: t('npi-type'), type: 'value' as const },
      { key: 'name', label: t('npi-name'), format: formatProviderName },
      {
        key: 'actions',
        type: 'actions' as const,
        delete: (_preferred: unknown) => {
          const preferred = _preferred as NpiProvider;
          void onRemovePreferred(preferred);
        },
      },
    ],
    []
  );

  return (
    <>
      <h3 className="input-label">{t('site-npi-preferred')}</h3>
      <FetchStates loading={loading}>
        <Table
          headers={headers}
          data={data?.preferred_npi_data ?? []}
          disabled={removingPreferred || refreshing}
        />
        <SiteNpiSearch
          disabled={removingPreferred || refreshing}
          onConfirmNewPreferred={refetch}
          currentPreferred={data?.preferred_npi_data}
        />
      </FetchStates>
    </>
  );
}
